import { useEffect, useState } from 'react';
import { V1RetirementDistributionType, V1TransferType } from '@fintronners/react-api/src';
import useUserAccount from '@/hooks/api/useUserAccount';
import useTransferService from './useTransferService';
import useCurrentAccountType, { useIsIRAAccount } from '../utils/useCurrentAccountType';
import { useWithholdings } from '@fintronners/internal-core/src/withholdingElections/hooks/useWithholdings';
import { getDistributionTypeFromWithdrawalSummary } from '@fintronners/internal-core/src/account/retirement/utils';
import useExternalAccounts, {
  AccountType,
} from '@fintronners/react-widgets/src/hooks/useExternalAccounts';
import useWithdrawalSummary from '@fintronners/react-widgets/src/hooks/useWithdrawalSummary';

type CreateWithdrawal = {
  amount: string;
  accountId?: string;
};

export const useWithdrawal = () => {
  const transferService = useTransferService();

  const { federalWithholdingRate, stateWithholdingRate, isFederalWaived, isStateWaived } =
    useWithholdings();
  const isIRAAccount = useIsIRAAccount();

  const accountType = useCurrentAccountType();
  const { userAccount } = useUserAccount(accountType);
  const { firstExternalAccount } = useExternalAccounts(AccountType.BROKERAGE, undefined, false);

  const [distributionType, setDistributionType] = useState<
    V1RetirementDistributionType | undefined
  >(undefined);

  const portfolioId = userAccount?.firstPortfolioId || '';
  const externalAccountId = firstExternalAccount?.id || '';
  const isPrematureWithdrawal = distributionType === V1RetirementDistributionType.Premature;

  const { getWithdrawalSummary } = useWithdrawalSummary({
    apiService: transferService,
    onSuccess: (data) => {
      const type = getDistributionTypeFromWithdrawalSummary(data);

      setDistributionType(type);
    },
  });

  useEffect(() => {
    if (!portfolioId || !externalAccountId) {
      return;
    }

    getWithdrawalSummary({
      portfolioUid: portfolioId,
      externalAccountUid: externalAccountId,
    });
  }, [portfolioId, externalAccountId]);

  const createWithdrawal = async ({ amount, accountId }: CreateWithdrawal) => {
    const retirementInfo = {
      distribution: {
        type: distributionType,
        ...(!isFederalWaived && {
          federalTaxWithholding: {
            amount: {
              percentage: String(federalWithholdingRate),
            },
          },
        }),
        ...(!isStateWaived && {
          stateTaxWithholding: {
            amount: {
              percentage: String(stateWithholdingRate),
            },
          },
        }),
      },
    };

    return await transferService.transferServiceCreateTransfer({
      externalAccountUid: accountId ?? '',
      type: V1TransferType.Withdraw,
      amount,
      portfolioUid: portfolioId ?? '',
      ...(isIRAAccount && {
        retirementInfo,
      }),
    });
  };

  return {
    createWithdrawal,
    isPrematureWithdrawal,
  };
};
